/**
 * Pure helpers voor visuele classificatie van planning-blokken.
 *
 * Werkbon-blokken krijgen een tint per type (onderhoud / service / project /
 * storing / installatie / overig). Dag-status `AFGEKEURD` voegt een rode rand
 * toe — afgekeurde dagen blijven zichtbaar in het bord (open punt voor fase
 * C1, waar dezelfde rule formeel doorgevoerd wordt voor verlofblokken).
 *
 * Leave-blokken zijn groen voor goedgekeurd, oranje voor aangevraagd. Dat is
 * de baseline voor de oranje/groen/rood-conventie uit handover §5.1; deze
 * port-PR neemt de twee statussen alvast over zonder de volle verlof-flow.
 */

import type { PlanbordFacturatieStatus } from '@streep/types'
import { verlofWeergaveBucket } from './verlof-weergave'

export type PlanningWerkbonType =
  | 'ONDERHOUD'
  | 'SERVICE'
  | 'PROJECT'
  | 'STORING'
  | 'INSTALLATIE'
  | string // Prisma enum staat in werkbonnen module; we leunen op de string-vorm

export type PlanningDayStatus =
  | 'GEPLAND'
  | 'IN_UITVOERING'
  | 'INGEDIEND'
  | 'GOEDGEKEURD'
  | 'AFGEKEURD'

// Twee-staps werkbon-goedkeuringsflow (intern → klant) die start zodra de
// werkbon AFGEROND is. Spiegelt de Prisma-enum `WerkbonGoedkeuringStatus` /
// het type uit @streep/types. `null` = (nog) geen flow / gereset na afkeur.
export type PlanningGoedkeuringStatus =
  | 'WACHT_OP_INTERN'
  | 'GOEDGEKEURD_INTERN'
  | 'AFGEKEURD_INTERN'
  | 'WACHT_OP_KLANT'
  | 'GOEDGEKEURD_KLANT'
  | 'AFGEKEURD_KLANT'

export type PlanningLeaveStatus =
  // V1 (legacy) statussen — bestaande data
  | 'AANGEVRAAGD'
  | 'GOEDGEKEURD'
  | 'AFGEWEZEN'
  // V2 statussen (verlofmodule-spec §5.2 — 9-stap workflow)
  | 'CONCEPT'
  | 'INGEDIEND'
  | 'GOEDGEKEURD_TEAMLEIDER'
  | 'AFGEKEURD_TEAMLEIDER'
  | 'GECLASSIFICEERD'
  | 'DEFINITIEF'
  | 'OVERRULED'
  | 'AFGEKEURD'
  | 'INGETROKKEN'
  | 'AANVULLENDE_VRAAG'

export type PlanningLeaveType = 'VAKANTIE' | 'ZIEK' | 'BIJZONDER' | 'ONBETAALD' | 'MEDISCH' | 'OVERIG' | 'ATV' | 'TVT_WERK' | 'TVT_REIS' | 'ZWANGERSCHAP' | 'GEBOORTE'

export interface BlockTone {
  /** Tailwind-klassen voor achtergrondvulling. */
  bg: string
  /** Tailwind-klassen voor randkleur (zonder `border` prefix). */
  border: string
  /** Tailwind-klassen voor tekstkleur. */
  text: string
  /** Korte label voor de a11y-titel. */
  label: string
}

// Kleuren-mapping per werkbon-type, conform de fs-pro legenda:
//   Service = blauw, Onderhoud = amber, Project = paars, Storing = rood,
//   Installatie = indigo. Verlof = groen (zie LEAVE_TONES).
const TYPE_TONES: Record<string, BlockTone> = {
  SERVICE: {
    bg: 'bg-blue-50',
    border: 'border-blue-500',
    text: 'text-blue-900',
    label: 'Service',
  },
  ONDERHOUD: {
    bg: 'bg-amber-50',
    border: 'border-amber-500',
    text: 'text-amber-900',
    label: 'Onderhoud',
  },
  PROJECT: {
    bg: 'bg-violet-50',
    border: 'border-violet-500',
    text: 'text-violet-900',
    label: 'Project',
  },
  STORING: {
    bg: 'bg-red-50',
    border: 'border-red-500',
    text: 'text-red-900',
    label: 'Storing',
  },
  INSTALLATIE: {
    bg: 'bg-indigo-50',
    border: 'border-indigo-500',
    text: 'text-indigo-900',
    label: 'Installatie',
  },
  // Kantoor/interne bonnen — neutrale grijze tint zodat ze visueel afwijken
  // van klantwerk (S3 §5.2). Geen factuurkleur, het is intern.
  KANTOOR: {
    bg: 'bg-slate-50',
    border: 'border-slate-400',
    text: 'text-slate-700',
    label: 'Kantoor',
  },
}

const FALLBACK_TONE: BlockTone = {
  bg: 'bg-slate-50',
  border: 'border-slate-300',
  text: 'text-slate-900',
  label: 'Werk',
}

const REJECTED_BORDER = 'border-rose-500'

// Goedkeuringsflow-randen op het planbord. We houden de type-tint als
// achtergrond (zodat het werkbon-type leesbaar blijft, net als bij AFGEKEURD)
// en overschrijven ALLEEN de rand zodat de status er bovenop zichtbaar is en
// geen achtergrond (bv. type- of BV-kleur) overschreven wordt.
//   - WACHT_OP_INTERN  → amber (afgerond, wacht op interne goedkeuring)
//   - GOEDGEKEURD_*    → emerald (intern/klant akkoord)
//   - AFGEKEURD_*      → rose (zelfde rand als dag-status AFGEKEURD)
//   - WACHT_OP_KLANT   → intern al goedgekeurd, dus emerald (verder in de flow)
const GOEDKEURING_BORDER: Record<PlanningGoedkeuringStatus, string> = {
  WACHT_OP_INTERN: 'border-amber-500',
  GOEDGEKEURD_INTERN: 'border-emerald-500',
  WACHT_OP_KLANT: 'border-emerald-500',
  GOEDGEKEURD_KLANT: 'border-emerald-500',
  AFGEKEURD_INTERN: REJECTED_BORDER,
  AFGEKEURD_KLANT: REJECTED_BORDER,
}

const GOEDKEURING_LABEL: Record<PlanningGoedkeuringStatus, string> = {
  WACHT_OP_INTERN: 'Wacht op goedkeuring',
  GOEDGEKEURD_INTERN: 'Goedgekeurd',
  WACHT_OP_KLANT: 'Goedgekeurd',
  GOEDGEKEURD_KLANT: 'Goedgekeurd',
  AFGEKEURD_INTERN: 'Afgekeurd',
  AFGEKEURD_KLANT: 'Afgekeurd',
}

export function getWerkbonBlockTone(
  werkbonType: PlanningWerkbonType,
  dayStatus: PlanningDayStatus,
  goedkeuringStatus?: PlanningGoedkeuringStatus | null,
): BlockTone {
  const base = TYPE_TONES[werkbonType.toUpperCase()] ?? FALLBACK_TONE
  // Goedkeuringsflow (werkbon-niveau) wint van de dag-status: zodra een
  // werkbon is afgerond en in de flow zit, toont het blok die status als rand
  // + label. De achtergrond (type-tint) blijft staan.
  if (goedkeuringStatus) {
    return {
      ...base,
      border: GOEDKEURING_BORDER[goedkeuringStatus],
      label: GOEDKEURING_LABEL[goedkeuringStatus],
    }
  }
  if (dayStatus === 'AFGEKEURD') {
    return { ...base, border: REJECTED_BORDER, label: 'Afgekeurd' }
  }
  return base
}

// Verlof-tones volgen verlofmodule-spec regel 23-25, 67:
//   - oranje: pending (INGEDIEND, GOEDGEKEURD_TEAMLEIDER, GECLASSIFICEERD, V1 AANGEVRAAGD)
//   - groen:  goedgekeurd-definitief (DEFINITIEF, V1 GOEDGEKEURD)
//   - rood:   AFGEKEURD-statussen — BLIJFT ZICHTBAAR als referentie (regel 25)
//   - donkerrood: type=ZIEK (regel 67) — onderscheid met afgekeurd
const PENDING_TONE: BlockTone = {
  bg: 'bg-orange-100',
  border: 'border-orange-400',
  text: 'text-orange-900',
  label: 'Verlof aangevraagd',
}
const APPROVED_TONE: BlockTone = {
  bg: 'bg-emerald-100',
  border: 'border-emerald-400',
  text: 'text-emerald-900',
  label: 'Verlof goedgekeurd',
}
const REJECTED_TONE: BlockTone = {
  bg: 'bg-rose-100',
  border: 'border-rose-500',
  text: 'text-rose-900',
  label: 'Verlof afgekeurd',
}
// F3.2 — Verzuim (ZIEK / MEDISCH) krijgt **donkerrood** zodat het visueel
// onmiskenbaar afwijkt van afgekeurd-verlof (lichtrood, bg-rose-100). De
// donkere bg + lichte tekst zorgen ook voor genoeg contrast op de planning-
// raster die zelf al een lichte achtergrond heeft.
//
// Bron: handover §5.4 + verzuim.md "Open punten / Planning-overlay donkerrood".
const SICK_TONE: BlockTone = {
  bg: 'bg-red-700',
  border: 'border-red-900',
  text: 'text-white',
  label: 'Ziek',
}
const HIDDEN_TONE: BlockTone = {
  bg: 'bg-slate-50',
  border: 'border-slate-200',
  text: 'text-slate-400',
  label: 'Verlof concept',
}

// FO-021 — de tone volgt de gedeelde "is verlof actief?"-regel (verlofWeergave-
// Bucket) zodat het planbord nooit uit de pas loopt met de weergave-query +
// conflict + capaciteit:
//   actief      → groen   (GOEDGEKEURD/DEFINITIEF)
//   aangevraagd → oranje  (AANGEVRAAGD/INGEDIEND/GOEDGEKEURD_TEAMLEIDER/
//                          GECLASSIFICEERD/AANVULLENDE_VRAAG)
//   geen        → de afkeur-statussen (AFGEWEZEN/AFGEKEURD/AFGEKEURD_TEAMLEIDER)
//                 + OVERRULED verschijnen niet meer als verlof-blok (de queries
//                 filteren ze weg); deze rode tone is nog slechts een defensieve
//                 fallback voor losse/legacy weergaves. CONCEPT en INGETROKKEN
//                 blijven neutraal verborgen.
const BUCKET_TONE: Record<'actief' | 'aangevraagd' | 'geen', BlockTone> = {
  actief: APPROVED_TONE,
  aangevraagd: PENDING_TONE,
  geen: REJECTED_TONE,
}

/**
 * Bepaalt de visuele tone voor een verlof-block in de planning.
 * Type=ZIEK krijgt altijd donkerrood (regel 67), ongeacht status — onderscheid met afgekeurd.
 */
export function getLeaveBlockTone(status: PlanningLeaveStatus, type?: PlanningLeaveType): BlockTone {
  if (type === 'ZIEK' || type === 'MEDISCH') return SICK_TONE
  // Niet-ingediend / ingetrokken: nooit een blok in de planning → neutraal verborgen.
  if (status === 'CONCEPT' || status === 'INGETROKKEN') return HIDDEN_TONE
  return BUCKET_TONE[verlofWeergaveBucket(status)]
}

/**
 * Stabiel kleurpalet per order — fs-pro-stijl. Elke orderId hashet naar één
 * van 8 vaste kleuren zodat verschillende orders binnen hetzelfde
 * werkbon-type visueel onderscheidbaar zijn op het planbord. fs-pro had
 * `ORDER_COLORS` als hard-coded record; v3 derived deterministisch uit de
 * order-id zodat het ook werkt voor orders die fs-pro nog niet kende.
 *
 * Hex-codes komen 1-op-1 uit fs-pro `mockPlanning.ORDER_COLORS`:
 *   blauw / rood / amber / paars / roze / teal / groen / oranje.
 */
export const ORDER_PALETTE: readonly string[] = [
  '#3B82F6', // blauw
  '#EF4444', // rood
  '#F59E0B', // amber
  '#8B5CF6', // paars
  '#EC4899', // roze
  '#14B8A6', // teal
  '#10B981', // groen
  '#F97316', // oranje
] as const

/** FNV-1a 32-bit hash — stabiel over runs / browsers. */
function hashString(input: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0
  }
  return h >>> 0
}

/** Pak een vaste kleur uit `ORDER_PALETTE` voor een gegeven id. */
export function getOrderColor(orderId: string | null | undefined): string {
  if (!orderId) return '#64748B' // slate-500 als fallback
  return ORDER_PALETTE[hashString(orderId) % ORDER_PALETTE.length]
}

/** Neutrale fallback-tint (STREEP surface-alt) als een BV geen kleur heeft (ADDENDA B.2). */
export const BV_FALLBACK_KLEUR = '#F0F1EC'

/** Operator-ingestelde BV-kleur is geldig als het een hex-code (#RGB of #RRGGBB) is. */
export function isValidBvKleur(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim())
}

/**
 * Resolve de blok-achtergrond (inline-style hex) voor een werkbon op het planbord:
 * de verlonende BV-kleur, of de neutrale fallback bij geen/ongeldige kleur (ADDENDA B.1/B.2).
 * Bewust een hex voor `style={{ backgroundColor }}` - geen Tailwind-class (JIT-safelist).
 */
export function resolveBvAchtergrond(kleur: string | null | undefined): string {
  return isValidBvKleur(kleur) ? kleur.trim() : BV_FALLBACK_KLEUR
}

// ─── batch-63 werkbon-facturatie (FO-472) — facturatie-markering op het blok ──
//
// Een klein, vast icoon rechtsonder op het werkbon-blok + een gedempte
// achtergrond bij "gefactureerd". Bewust GEEN extra kleur op rand/achtergrond:
// type- en goedkeuringskleuren (`getWerkbonBlockTone`) blijven leidend (A1).

export type FacturatieIcoon = 'vinkje' | 'vliegtuig' | 'half' | 'streepje'

export interface FacturatieMarkering {
  /** Icoon rechtsonder; `null` = geen icoon (nog te factureren). */
  icoon: FacturatieIcoon | null
  /** Achtergrond dempen (zelfde tint, lagere verzadiging). */
  gedempt: boolean
  /** Tailwind-klassen van het icoon-rondje (rand + vulling + tekst). */
  cls: string
  /** Label voor legenda en a11y. */
  label: string
}

const FACTURATIE_MARKERING: Record<PlanbordFacturatieStatus, FacturatieMarkering> = {
  GEFACTUREERD: {
    icoon: 'vinkje',
    gedempt: true,
    cls: 'border-emerald-600 bg-emerald-50 text-emerald-700',
    label: 'Gefactureerd',
  },
  VERZONDEN_FINQLE: {
    icoon: 'vliegtuig',
    gedempt: false,
    cls: 'border-sky-600 bg-sky-50 text-sky-700',
    label: 'Verzonden naar Finqle',
  },
  DEELS_GEFACTUREERD: {
    icoon: 'half',
    gedempt: false,
    cls: 'border-emerald-600 bg-white text-emerald-700',
    label: 'Deels gefactureerd',
  },
  TE_FACTUREREN: {
    icoon: null,
    gedempt: false,
    cls: '',
    label: 'Nog te factureren',
  },
  NIET_FACTURABEL: {
    icoon: 'streepje',
    gedempt: false,
    cls: 'border-slate-500 bg-slate-50 text-slate-600',
    label: 'Niet te factureren',
  },
}

/** De markering van een planbord-facturatiestatus (icoon, demping, legenda-label). */
export function getFacturatieMarkering(status: PlanbordFacturatieStatus): FacturatieMarkering {
  return FACTURATIE_MARKERING[status]
}

/** Volgorde van de legenda-items (zelfde volgorde als de statussen op het bord). */
export const FACTURATIE_LEGENDA_VOLGORDE: readonly PlanbordFacturatieStatus[] = [
  'TE_FACTUREREN',
  'VERZONDEN_FINQLE',
  'DEELS_GEFACTUREERD',
  'GEFACTUREERD',
  'NIET_FACTURABEL',
]

function hexNaarRgb(hex: string): [number, number, number] {
  const h = hex.trim().replace('#', '')
  const vol = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
  return [
    Number.parseInt(vol.slice(0, 2), 16),
    Number.parseInt(vol.slice(2, 4), 16),
    Number.parseInt(vol.slice(4, 6), 16),
  ]
}

function rgbNaarHex([r, g, b]: [number, number, number]): string {
  const deel = (n: number) => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0')
  return `#${deel(r)}${deel(g)}${deel(b)}`.toUpperCase()
}

/**
 * De gedempte variant van een blok-achtergrond (hex): zelfde tint, lagere
 * verzadiging en iets lichter, zodat "gefactureerd" in één oogopslag
 * terugzakt zonder een nieuwe kleur te introduceren. Ongeldige invoer valt
 * terug op de neutrale BV-fallback.
 */
export function dempBlokAchtergrond(hex: string | null | undefined): string {
  const basis = isValidBvKleur(hex) ? hex : BV_FALLBACK_KLEUR
  const [r, g, b] = hexNaarRgb(basis)
  // Luminantie-grijs van dezelfde kleur, dan 60% richting dat grijs (verzadiging
  // omlaag) en 25% richting wit (iets lichter).
  const grijs = 0.299 * r + 0.587 * g + 0.114 * b
  const ontzadig = (c: number) => c + (grijs - c) * 0.6
  const lichter = (c: number) => c + (255 - c) * 0.25
  return rgbNaarHex([lichter(ontzadig(r)), lichter(ontzadig(g)), lichter(ontzadig(b))])
}
